/** The cap on a stored original. R2 has no transforms, so bytes are bounded at the source. */
export const MAX_ORIGINAL_PX = 1600;

/** The second object every upload writes. Covers EntityAvatar and any future list thumbnail. */
export const THUMB_PX = 256;

export const WEBP_QUALITY = 0.85;

/**
 * Scale to fit inside `max` on the longest side, preserving aspect ratio and never upscaling.
 * Split out from the canvas work because this is the part with branches — jsdom has no canvas,
 * so it is also the only part that can be tested.
 */
export const scaledDimensions = (width: number, height: number, max: number) => {
  const longest = Math.max(width, height);
  if (longest <= max) return { width, height };

  const ratio = max / longest;
  return {
    // A rounded-down dimension can reach 0 on an extreme aspect ratio, and a 0-width canvas
    // throws on toBlob — so the floor is 1 pixel, not 0.
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
};

export const scaleCanvas = (source: HTMLCanvasElement, max: number): HTMLCanvasElement => {
  const { width, height } = scaledDimensions(source.width, source.height, max);
  const target = document.createElement('canvas');
  target.width = width;
  target.height = height;
  target.getContext('2d')!.drawImage(source, 0, 0, width, height);
  return target;
};

/**
 * WebP for every upload: it is the smallest of the formats every target browser decodes, and it
 * makes the stored extension a constant — which is why the key carries none.
 */
export const canvasToWebp = (canvas: HTMLCanvasElement, quality = WEBP_QUALITY): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas produced no blob'))),
      'image/webp',
      quality
    );
  });
